/**
 * Cloud Reconnection Service
 * 
 * Handles automatic reconnection with exponential backoff for cloud providers
 */

import cloudTokenService from './cloudTokenService'

class CloudReconnectionService {
  constructor() {
    this.reconnectTimerRef = null
    this.reconnectDelayRef = 2000 // start with 2s, backoff up to 60s
    this.retryCountRef = 0 // Track retry attempts
    this.maxRetries = 10 // Maximum number of retry attempts
  }

  /**
   * Set up automatic reconnection timer
   */
  setupReconnectionTimer(accessToken, refreshToken, isOnboarding = false) {
    // Skip reconnection timer setup during onboarding to prevent unnecessary API calls
    if (isOnboarding) {
      console.log('🔒 CloudReconnectionService: Onboarding detected, skipping reconnection timer setup')
      return
    }
    
    // Only set up reconnection if we have tokens
    if (accessToken && refreshToken) {
      console.log('⏰ CloudReconnectionService: Setting up automatic reconnection timer')
      this.scheduleReconnectAttempt()
    }
  }

  /**
   * Schedule a reconnection attempt
   */
  scheduleReconnectAttempt(isOnboarding = false) {
    // Skip reconnection scheduling during onboarding to prevent unnecessary API calls
    if (isOnboarding) {
      console.log('🔒 CloudReconnectionService: Onboarding detected, skipping reconnection scheduling')
      return
    }
    
    if (this.reconnectTimerRef) {
      clearTimeout(this.reconnectTimerRef)
    }
    
    this.reconnectTimerRef = setTimeout(async () => {
      try {
        console.log('🔄 CloudReconnectionService: Attempting automatic reconnection...')
        
        if (this.retryCountRef >= this.maxRetries) {
          console.log('❌ CloudReconnectionService: Max reconnection attempts reached')
          return
        }
        
        const newAccessToken = await cloudTokenService.refreshAccessToken()
        if (newAccessToken) {
          console.log('✅ CloudReconnectionService: Automatic reconnection successful')
          this.retryCountRef = 0
          this.reconnectDelayRef = 2000
        } else {
          this.retryCountRef++
          this.reconnectDelayRef = Math.min(this.reconnectDelayRef * 2, 30000)
          // Don't schedule reconnection during onboarding
          const isOnboarding = window.location.pathname.includes('/onboarding')
          if (!isOnboarding) {
            this.scheduleReconnectAttempt()
          }
        }
        
      } catch (error) {
        console.error('❌ CloudReconnectionService: Automatic reconnection failed:', error)
        this.retryCountRef++
        this.reconnectDelayRef = Math.min(this.reconnectDelayRef * 2, 30000)
        // Don't schedule reconnection during onboarding
        const isOnboarding = window.location.pathname.includes('/onboarding')
        if (!isOnboarding) {
          this.scheduleReconnectAttempt()
        }
      }
    }, this.reconnectDelayRef)
  }

  /**
   * Reset reconnection state (useful for manual recovery)
   */
  resetReconnectionState(isOnboarding = false) {
    // Skip operations during onboarding to prevent unnecessary API calls
    if (isOnboarding) {
      console.log('🔒 CloudReconnectionService: Onboarding detected, skipping reconnection state reset')
      return
    }
    
    console.log('🔄 CloudReconnectionService: Manually resetting reconnection state')
    this.retryCountRef = 0
    this.reconnectDelayRef = 2000
    
    // Clear any pending reconnection timer
    if (this.reconnectTimerRef) {
      clearTimeout(this.reconnectTimerRef)
      this.reconnectTimerRef = null
    }
  }

  /**
   * Force stop all reconnection attempts (emergency stop)
   */
  stopReconnectionAttempts(isOnboarding = false) {
    // Skip operations during onboarding to prevent unnecessary API calls
    if (isOnboarding) {
      console.log('🔒 CloudReconnectionService: Onboarding detected, skipping reconnection attempts stop')
      return
    }
    
    console.log('🛑 CloudReconnectionService: Force stopping all reconnection attempts')
    this.retryCountRef = this.maxRetries
    this.reconnectDelayRef = 2000
    
    // Clear any pending reconnection timer
    if (this.reconnectTimerRef) {
      clearTimeout(this.reconnectTimerRef)
      this.reconnectTimerRef = null
    }
  }

  /**
   * Cancel pending reconnection attempts
   */
  cancelReconnectionAttempts() {
    if (this.reconnectTimerRef) {
      clearTimeout(this.reconnectTimerRef)
      this.reconnectTimerRef = null
      console.log('🔄 CloudReconnectionService: Cancelled pending reconnect attempt')
    }
  }

  /**
   * Reset retry count and delay on successful token refresh
   */
  onSuccessfulTokenRefresh() {
    this.retryCountRef = 0
    this.reconnectDelayRef = 2000
    
    // Cancel any pending reconnect attempts since token was refreshed successfully
    this.cancelReconnectionAttempts()
    console.log('🔄 CloudReconnectionService: Cancelled pending reconnect attempt due to successful token refresh')
  }

  /**
   * Get current reconnection state
   */
  getReconnectionState() {
    return {
      retryCount: this.retryCountRef,
      reconnectDelay: this.reconnectDelayRef,
      maxRetries: this.maxRetries,
      hasPendingAttempt: !!this.reconnectTimerRef
    }
  }

  /**
   * Cleanup resources
   */
  cleanup() {
    if (this.reconnectTimerRef) {
      clearTimeout(this.reconnectTimerRef)
      this.reconnectTimerRef = null
    }
  }
}

export default new CloudReconnectionService()
